import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TESTID } from "@/lib/testIds";
import { Mail, Lock, Shield, ShieldCheck, ArrowRight, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";

const LOGIN_BG = "https://static.prod-images.emergentagent.com/jobs/106dfe78-39aa-4b25-90de-4fde5ec80481/images/a20b61196d94984d78db8f8f839cfbcac3860620dd922ea9387d6159b9b58bc9.png";

export default function LoginPage() {
  const [mode, setMode] = useState("magic"); // 'magic' or 'password'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [bridgeFallback, setBridgeFallback] = useState(null); // {magic_url, expires_at, reason}
  const [bridgeCopied, setBridgeCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const { loginWithPassword, requestMagicLink } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const submitAuthenticator = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      // Step 1: password login (yields mfa-pending token if mfa_enabled)
      const res = await loginWithPassword(email, password);
      if (!res.mfaRequired) {
        // Account has no MFA → password alone got them in. Surface a warning.
        setInfo("This account has no Authenticator configured. You're signed in via password only — enable Authenticator in Security for 25D-grade auth.");
        navigate(from, { replace: true });
        return;
      }
      // Step 2: post the 6-digit code to /mfa/verify-login
      const { default: api } = await import("@/lib/api");
      const verify = await api.post("/auth/mfa/verify-login", { code: totpCode });
      const data = verify.data;
      // Persist tokens via AuthContext setter pattern (mirrors loginWithPassword)
      localStorage.setItem("armada_access_token", data.access_token);
      localStorage.setItem("armada_refresh_token", data.refresh_token);
      window.location.href = from;
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      setError(detail || err.message || "Authenticator login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      const res = await loginWithPassword(email, password);
      if (res.mfaRequired) {
        navigate("/auth/mfa-challenge");
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setBridgeFallback(null); setBridgeCopied(false); setLoading(true);
    try {
      const res = await requestMagicLink(email);
      setInfo("If an account exists for that email, a magic link has been dispatched. Check your inbox.");
      if (res?.engage_the_bridge?.magic_url) {
        setBridgeFallback(res.engage_the_bridge);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const engageTheBridge = async () => {
    if (!bridgeFallback?.magic_url) return;
    try {
      await navigator.clipboard.writeText(bridgeFallback.magic_url);
      setBridgeCopied(true);
      setTimeout(() => setBridgeCopied(false), 2500);
    } catch {
      // clipboard blocked — open it directly
      window.location.href = bridgeFallback.magic_url;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-armada-base">
      {/* Background silk */}
      <div className="absolute inset-0">
        <img src={LOGIN_BG} alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/80" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 dashboard-grid opacity-30" />

      <div className="relative min-h-screen flex flex-col">
        {/* Top brand */}
        <div className="px-8 lg:px-16 pt-10 animate-fade-up">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A88729] flex items-center justify-center glow-gold">
              <Shield className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold">EIGOO ARMADA</div>
              <div className="text-sm font-bold text-white -mt-0.5">Sovereign Command Auth</div>
            </div>
          </div>
        </div>

        {/* Center card */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-[460px] animate-fade-up stagger-2">
            <div className="glass-strong rounded-2xl p-8 md:p-10">
              {/* Heading */}
              <div className="mb-8">
                <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> ACCESS THE FLEET
                </div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-white">
                  Enter the <span className="gold-shimmer">Armada</span>
                </h1>
                <p className="text-sm text-zinc-400 mt-2 font-light">
                  One sovereign login for the entire $900M empire.
                </p>
              </div>

              {/* Social Sign-In — high visibility, above mode toggle */}
              <div className="space-y-2 mb-5">
                <button
                  type="button"
                  data-testid="login-google-btn"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + "/dashboard";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-md bg-white text-black font-bold hover:bg-zinc-100 transition-all shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_25px_rgba(255,255,255,0.25)]"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continue with Google</span>
                </button>
                <button
                  type="button"
                  data-testid="login-facebook-btn"
                  onClick={() => setError("Facebook OAuth coming online — awaiting App credentials. Use Google, Magic Link, or Password.")}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-md bg-[#1877F2] text-white font-bold hover:bg-[#1865d8] transition-all opacity-90 hover:opacity-100"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span>Continue with Facebook</span>
                </button>
              </div>

              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] tracking-armada text-zinc-500 font-bold">OR</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Mode toggle */}
              <div className="flex gap-1 p-1 rounded-lg bg-black/40 border border-white/5 mb-6">
                <button
                  data-testid={TESTID.loginMagicLinkToggle}
                  onClick={() => { setMode("magic"); setError(""); setInfo(""); }}
                  className={`flex-1 py-2 px-3 rounded-md text-xs font-bold tracking-wider uppercase transition-all ${
                    mode === "magic" ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Mail className="w-3.5 h-3.5 inline mr-1.5" /> Magic Link
                </button>
                <button
                  data-testid={TESTID.loginPasswordToggle}
                  onClick={() => { setMode("password"); setError(""); setInfo(""); }}
                  className={`flex-1 py-2 px-3 rounded-md text-xs font-bold tracking-wider uppercase transition-all ${
                    mode === "password" ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Lock className="w-3.5 h-3.5 inline mr-1.5" /> Password
                </button>
                <button
                  data-testid="login-authenticator-toggle"
                  onClick={() => { setMode("authenticator"); setError(""); setInfo(""); }}
                  className={`flex-1 py-2 px-3 rounded-md text-xs font-bold tracking-wider uppercase transition-all ${
                    mode === "authenticator" ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5" /> Authenticator
                </button>
              </div>

              {error && (
                <div data-testid={TESTID.loginError} className="mb-4 flex items-start gap-2 p-3 rounded-md bg-red-950/40 border border-red-900/50 text-red-200 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {info && (
                <div data-testid={TESTID.loginSuccessBanner} className="mb-4 flex items-start gap-2 p-3 rounded-md bg-emerald-950/40 border border-emerald-900/50 text-emerald-200 text-sm">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{info}</span>
                </div>
              )}

              {mode === "magic" && (
                <form onSubmit={submitMagic} className="space-y-4">
                  <div>
                    <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Commander Email</label>
                    <input
                      data-testid={TESTID.loginMagicEmail}
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="callsign@eigooinc.net"
                      className="armada-input mt-1"
                    />
                  </div>
                  <button
                    data-testid={TESTID.loginMagicSubmit}
                    type="submit"
                    disabled={loading || !email}
                    className="btn-gold w-full flex items-center justify-center gap-2"
                  >
                    {loading ? "Dispatching…" : "Send Magic Link"}
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  {bridgeFallback && (
                    <div
                      data-testid="engage-the-bridge-panel"
                      className="mt-4 p-4 rounded-lg border border-amber-400/40 bg-amber-400/5"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                        <div className="text-[10px] tracking-armada text-amber-300 font-bold">
                          ENGAGE THE BRIDGE · FALLBACK CHANNEL
                        </div>
                      </div>
                      <p className="text-xs text-zinc-400 leading-relaxed mb-3">
                        Email delivery is offline (Resend not configured). Use this one-time
                        link directly or pass it via secure channel. It expires in 15 minutes.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          data-testid="engage-the-bridge-copy"
                          onClick={engageTheBridge}
                          className="flex-1 px-3 py-2 rounded-md bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/40 text-amber-200 text-xs font-bold tracking-wider uppercase transition"
                        >
                          {bridgeCopied ? "✓ Link Copied" : "Copy Magic Link"}
                        </button>
                        <a
                          href={bridgeFallback.magic_url}
                          data-testid="engage-the-bridge-open"
                          className="flex-1 px-3 py-2 rounded-md bg-black/40 hover:bg-black/60 border border-white/10 text-white text-xs font-bold tracking-wider uppercase text-center transition"
                        >
                          Open Now
                        </a>
                      </div>
                    </div>
                  )}
                </form>
              )}

              {mode === "password" && (
                <form onSubmit={submitPassword} className="space-y-4">
                  <div>
                    <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Commander Email</label>
                    <input
                      data-testid={TESTID.loginEmail}
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="callsign@eigooinc.net"
                      className="armada-input mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Password</label>
                    <input
                      data-testid={TESTID.loginPassword}
                      type="password"
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="armada-input mt-1"
                    />
                  </div>
                  <button
                    data-testid={TESTID.loginSubmit}
                    type="submit"
                    disabled={loading || !email || !password}
                    className="btn-gold w-full flex items-center justify-center gap-2"
                  >
                    {loading ? "Authenticating…" : "Authenticate"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <div className="text-center">
                    <Link
                      data-testid={TESTID.loginForgotLink}
                      to="/auth/forgot-password"
                      className="text-xs text-zinc-400 hover:text-[#D4AF37] transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </form>
              )}

              {mode === "authenticator" && (
                <form data-testid="authenticator-form" onSubmit={submitAuthenticator} className="space-y-4">
                  <div className="p-3 rounded-md bg-[#D4AF37]/5 border border-[#D4AF37]/20 flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div className="text-[11px] text-zinc-300 leading-relaxed">
                      25D-grade login · Combine your password with the 6-digit code from your authenticator app (Google Authenticator, 1Password, Authy, etc.). MFA must already be enabled in Security.
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Commander Email</label>
                    <input
                      data-testid="authenticator-email"
                      type="email" required autoComplete="email"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="callsign@eigooinc.net" className="armada-input mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">Password</label>
                    <input
                      data-testid="authenticator-password"
                      type="password" required autoComplete="current-password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••" className="armada-input mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold tracking-wider uppercase text-zinc-400">6-digit Authenticator Code</label>
                    <input
                      data-testid="authenticator-code"
                      type="text" required inputMode="numeric" pattern="[0-9]*" maxLength={6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123 456"
                      className="armada-input mt-1 text-center tracking-[0.5em] text-xl font-mono"
                    />
                  </div>
                  <button
                    data-testid="authenticator-submit"
                    type="submit"
                    disabled={loading || !email || !password || totpCode.length !== 6}
                    className="btn-gold w-full flex items-center justify-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    {loading ? "Verifying…" : "Authenticate with TOTP"}
                  </button>
                </form>
              )}
            </div>

            {/* Footer brand */}
            <div className="text-center mt-8 text-[10px] tracking-armada text-zinc-600 font-semibold">
              SIREN · SLAYMARIE · MERCY · VANGUARD · CYBEROPS
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
